import { useCallback } from "react";
import { showDailyLimitPaywall, type PaywallMoment } from "@/hooks/usePaywallDialog";
import { guardedGate } from "@/lib/downloadGate";
import { useUsageGate } from "@/hooks/useUsageGate";

/**
 * For tools whose real "value moment" is a discrete action (a button, a
 * download, a merge) -- call `logUsageNow()` right before doing the work and
 * stop if it returns false.
 *
 * Returns whether the daily limit allowed it (false means the paywall was
 * already shown -- the caller should stop and not proceed).
 *
 * The decision now comes from the shared cached usage count (see
 * lib/usageGate.ts), not a fresh server request per click: it resolves
 * immediately when the cache says the use is allowed and logs in the
 * background. The old version awaited a usage.check round trip on every
 * click -- QA measured 0.5 s to over 6 s with no busy state, and a result
 * arriving late could overwrite text typed after the click (CC-10/TR-12).
 *
 * Also what multi-file batch tools (Merge PDF and similar) pair with
 * FileDropZone's `deferUsageLog`: adding or removing files while tidying
 * the list isn't a use, the merge itself is.
 */
export function useLogUsageOnAction() {
  const { known, tryUse, tryUseExport, toolId } = useUsageGate();

  const logUsageNow = useCallback(async (moment?: PaywallMoment): Promise<boolean> => {
    if (!known) return true;
    // Never silent (B-0309): a check that fails or hangs ends in a message (or, for a paid session, in going on).
    return guardedGate(async () => {
      const decision = await tryUse();
      if (decision === "blocked") {
        showDailyLimitPaywall(toolId, moment);
        return false;
      }
      return true;
    }, { explainFalse: true });
  }, [known, tryUse, toolId]);

  /**
   * For tools where the person can keep editing and exporting in one visit: call at Download with a key made
   * from the export's inputs (exportKey in lib/exportCharge). The same key again is free; changed content
   * costs another use. Returns false when the paywall was shown.
   */
  const logExportNow = useCallback(async (key: string): Promise<boolean> => {
    if (!known) return true;
    return guardedGate(async () => {
      const decision = await tryUseExport(key);
      if (decision === "blocked") {
        showDailyLimitPaywall(toolId, "download");
        return false;
      }
      return true;
    }, { explainFalse: true });
  }, [known, tryUseExport, toolId]);

  return { logUsageNow, logExportNow };
}
